/**
 * useDefenseSelection
 *
 * Reconciliation logic for the Simulator's Defense_Selection: looks up the active
 * Custom_Defense (if any) from the shared CustomDefenseContext, computes whether it was
 * built for a different formation than the current Simulator formation, exposes the two
 * selection handlers (built-in vs. custom), and reconciles the selection back to the `man`
 * Built_In_Coverage if the previously-selected custom defense no longer resolves (e.g. it
 * was deleted).
 *
 * Requirements: 3.1, 3.2, 5.3, 7.1, 7.2, 7.3
 */

import { useEffect } from 'react';
import { useCustomDefenses, useSimulation } from '../context';
import { CoverageType, CustomDefense, FormationType } from '../types';

export interface UseDefenseSelectionResult {
  /** The currently selected Custom_Defense, looked up from completeDefenses by id, or
   *  undefined when a Built_In_Coverage is the active Defense_Selection (or the previously
   *  selected id no longer resolves). */
  activeCustomDefense: CustomDefense | undefined;
  /** True when a Custom_Defense is selected and its saved formation differs from
   *  currentFormation (Req 5.3). */
  isFormationMismatch: boolean;
  /** Selects a Built_In_Coverage: sets selectedCoverage and clears selectedCustomDefenseId. */
  handleSelectBuiltIn: (coverage: CoverageType) => void;
  /** Selects a Complete_Defense: sets selectedCustomDefenseId. */
  handleSelectCustomDefense: (defense: CustomDefense) => void;
}

export function useDefenseSelection(currentFormation: FormationType): UseDefenseSelectionResult {
  const { completeDefenses } = useCustomDefenses();
  const { state, setSelectedCoverage, setSelectedCustomDefenseId } = useSimulation();

  const activeCustomDefense = state.selectedCustomDefenseId
    ? completeDefenses.find((d) => d.id === state.selectedCustomDefenseId)
    : undefined;

  // Req 7.1/7.2/7.3: if the selected id no longer resolves (e.g. deleted), fall back to 'man'.
  useEffect(() => {
    if (state.selectedCustomDefenseId && !activeCustomDefense) {
      setSelectedCoverage('man');
      setSelectedCustomDefenseId(null);
    }
  }, [state.selectedCustomDefenseId, activeCustomDefense, setSelectedCoverage, setSelectedCustomDefenseId]);

  const isFormationMismatch = !!activeCustomDefense && activeCustomDefense.formation !== currentFormation;

  const handleSelectBuiltIn = (coverage: CoverageType) => {
    setSelectedCoverage(coverage);
    setSelectedCustomDefenseId(null);
  };

  const handleSelectCustomDefense = (defense: CustomDefense) => {
    setSelectedCustomDefenseId(defense.id!);
  };

  return {
    activeCustomDefense,
    isFormationMismatch,
    handleSelectBuiltIn,
    handleSelectCustomDefense,
  };
}
